/**
 * Recovering from a failed lazy-chunk load without looping.
 *
 * A deploy replaces chunk hashes, so a tab opened before it 404s on the next
 * lazy import, and reloading fetches the new build. But a chunk that is missing
 * from the new build too fails again straight after the reload, and reloading
 * on every failure turns that into an endless refresh. One reload per window
 * covers the deploy case; a second failure inside it is a real error, left to
 * surface on the app's error screen.
 */
export const PRELOAD_RELOAD_KEY = 'banksheets:preload-reload-at'
export const PRELOAD_RELOAD_WINDOW_MS = 60_000

/** Whether a failed chunk load should reload, given when the last such reload happened. */
export function shouldReloadAfterPreloadError(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || now - lastReloadAt >= PRELOAD_RELOAD_WINDOW_MS
}

/**
 * Reload once per window, recording the reload in sessionStorage.
 *
 * Returns whether it reloaded. When storage cannot be read or written it does
 * not reload: without a record of the last reload, every failure would reload
 * again, which is the loop this exists to prevent.
 */
export function reloadAfterPreloadError(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  reload: () => void,
  now = Date.now()
): boolean {
  try {
    const raw = storage.getItem(PRELOAD_RELOAD_KEY)
    const lastReloadAt = raw === null ? null : Number(raw)
    if (!shouldReloadAfterPreloadError(Number.isFinite(lastReloadAt) ? lastReloadAt : null, now)) {
      return false
    }
    storage.setItem(PRELOAD_RELOAD_KEY, String(now))
  } catch {
    return false
  }
  reload()
  return true
}
